import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from '../services/api';
import { Target, TrendingUp, BarChart3, Settings, Download, Share2, Check, Eye, EyeOff, BarChart2, Zap, CheckSquare, Square } from 'lucide-react';
import { ChartErrorBoundary } from '../components/ErrorBoundary';
import { UploadStep } from '../components/analytics/UploadStep';
import { ShotGroupTable } from '../components/analytics/ShotGroupTable';
import { DispersionChart } from '../components/analytics/DispersionChart';
import { TrajectoryChart } from '../components/analytics/TrajectoryChart';
import { GolferPOVTrajectory } from '../components/analytics/GolferPOVTrajectory';
import { OptimizerChart } from '../components/analytics/OptimizerChart';
import { AverageMetricsBar } from '../components/analytics/AverageMetricsBar';
import { CoachingModal } from '../components/analytics/CoachingModal';
import { AIRecommendationsView } from '../components/analytics/AIRecommendationsView';
import { Button } from '../components/ds/Button';
import { exportSessionToCSV, shareSession, downloadTextReport } from '../utils/exportUtils';

const VIEWS = [
  { id: 'dispersion', label: 'Dispersion', icon: Target },
  { id: 'trajectory', label: 'Trajectory', icon: TrendingUp },
  { id: 'golferpov', label: 'Golfer POV', icon: Eye },
  { id: 'ai', label: 'Simulator', icon: Zap },
];

export default function SessionAnalyticsPage() {
  const location = useLocation();
  const [sessionData, setSessionData] = useState(null);
  const [activeView, setActiveView] = useState('dispersion');
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [showCoachingModal, setShowCoachingModal] = useState(false);

  // TrackMan-style controls
  const [distanceType, setDistanceType] = useState('carry'); // 'carry' or 'total'
  const [visibleGroupIds, setVisibleGroupIds] = useState(new Set());
  const [selectedShots, setSelectedShots] = useState([]); // [{ groupId, shotNumber }, ...]
  const [showAverage, setShowAverage] = useState(true);

  // Handle prefill data from History navigation
  useEffect(() => {
    const prefillData = location.state?.prefillData;
    if (prefillData && !sessionData) {
      // Transform single-shot prefillData into sessionData structure
      const historicalSession = {
        shotGroups: [{
          id: 'historical-session',
          name: `Historical ${prefillData.club_type || 'Driver'}`,
          color: '#3b82f6',
          shots: [{
            shotNumber: 1,
            included: true,
            metrics: {
              ballSpeed: prefillData.ball_speed,
              clubSpeed: prefillData.club_speed,
              launchAngle: prefillData.launch_angle,
              spinRate: prefillData.spin_rate,
              spinAxis: prefillData.spin_axis,
              attackAngle: prefillData.attack_angle,
              carryDistance: prefillData.carry || null,
              totalDistance: prefillData.total || null,
              offlineDistance: prefillData.offline || null,
            }
          }]
        }]
      };

      setSessionData(historicalSession);
      setSelectedGroupId('historical-session');
      setVisibleGroupIds(new Set(['historical-session']));
    }
  }, [location.state, sessionData]);

  // Handle LinksID club export via URL param (from Apps tab)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const clubExport = params.get('linksid_club_export');
    const linksidSession = params.get('linksid_session');
    if (sessionData) return;

    let exportUrl = null;
    if (clubExport) {
      exportUrl = `https://linksid.colewinds-dev.com/sessions/export-club?${clubExport}`;
    } else if (linksidSession) {
      exportUrl = `https://linksid.colewinds-dev.com/sessions/${linksidSession}/export`;
    }

    if (exportUrl) {
      fetch(exportUrl)
        .then(res => {
          if (!res.ok) throw new Error('Failed to load LinksID data');
          return res.json();
        })
        .then(data => {
          const imported = { shotGroups: data.shotGroups || [] };
          setSessionData(imported);
          if (imported.shotGroups.length > 0) {
            setSelectedGroupId(imported.shotGroups[0].id);
            setVisibleGroupIds(new Set(imported.shotGroups.map(g => g.id)));
          }
        })
        .catch(err => {
          console.error('LinksID import failed:', err);
        });
    }
  }, [location.search, sessionData]);

  const handleUploadComplete = (data) => {
    setSessionData(data.session);
    if (data.session?.shotGroups?.length > 0) {
      setSelectedGroupId(data.session.shotGroups[0].id);
      // Initialize all groups as visible
      setVisibleGroupIds(new Set(data.session.shotGroups.map(g => g.id)));
    }
  };

  const toggleGroupVisibility = (groupId) => {
    setVisibleGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const handleToggleShot = (groupId, shotNumber) => {
    setSessionData(prev => ({
      ...prev,
      shotGroups: prev.shotGroups.map(group =>
        group.id === groupId
          ? {
              ...group,
              shots: group.shots.map(shot =>
                shot.shotNumber === shotNumber
                  ? { ...shot, included: !shot.included }
                  : shot
              ),
            }
          : group
      ),
    }));
  };

  const handleSelectShot = (groupId, shotNumber) => {
    setSelectedShots(prev => {
      const shotIndex = prev.findIndex(
        s => s.groupId === groupId && s.shotNumber === shotNumber
      );

      if (shotIndex >= 0) {
        // Already selected - remove it
        return prev.filter((_, idx) => idx !== shotIndex);
      } else {
        // Not selected - add it
        return [...prev, { groupId, shotNumber }];
      }
    });
  };

  const handleSelectAll = () => {
    const allShots = sessionData.shotGroups.flatMap(group =>
      group.shots.filter(s => s.included).map(shot => ({
        groupId: group.id,
        shotNumber: shot.shotNumber
      }))
    );
    setSelectedShots(allShots);
  };

  const handleDeselectAll = () => {
    setSelectedShots([]);
  };

  const handleExport = () => {
    // Export as CSV
    exportSessionToCSV(sessionData);
  };

  const handleShare = async () => {
    const result = await shareSession(sessionData);
    if (result.success) {
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 2000);
    }
  };

  const handleSaveSession = async () => {
    try {
      const response = await axios.post('/history', {
        sessionData: sessionData,
        session_type: 'analytics'
      });

      alert('Session saved to history!');
    } catch (error) {
      console.error('Save session error:', error);
      alert('Failed to save session. Please try again.');
    }
  };

  // Upload step - no data yet
  if (!sessionData) {
    return (
      <div className="min-h-screen bg-background py-12">
        <UploadStep onUploadComplete={handleUploadComplete} />
      </div>
    );
  }

  // Filter groups by visibility
  const visibleGroups = sessionData?.shotGroups?.filter(g => visibleGroupIds.has(g.id)) || [];

  // Get the reference shot (#1 from baseline group or first group)
  const baselineGroup = sessionData.shotGroups.find(g => g.isBaseline) || sessionData.shotGroups[0];
  const referenceShot = baselineGroup?.shots[0];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-surface border-b border-border sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-text-primary">
                {sessionData.name}
              </h1>
              <p className="text-sm text-text-secondary">{sessionData.date}</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleSaveSession}
                variant="primary"
                size="md"
                icon={Check}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Save Session
              </Button>
              <Button
                onClick={handleShare}
                variant="secondary"
                size="md"
                icon={shareSuccess ? Check : Share2}
                className={shareSuccess ? 'border-success bg-success/20 text-success' : ''}
              >
                {shareSuccess ? 'Copied!' : 'Copy Link'}
              </Button>
              <Button
                onClick={handleExport}
                variant="primary"
                size="md"
                icon={Download}
              >
                Export CSV
              </Button>
            </div>
          </div>

          {/* Shot Group Selector with Visibility Toggles */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
            {sessionData.shotGroups.map(group => (
              <div
                key={group.id}
                className={`flex-shrink-0 rounded-lg border-2 transition-all ${
                  selectedGroupId === group.id
                    ? 'border-primary bg-primary/20'
                    : 'border-border bg-surface'
                } ${!visibleGroupIds.has(group.id) ? 'opacity-50' : ''}`}
              >
                <button
                  onClick={() => setSelectedGroupId(group.id)}
                  className="flex items-center gap-2 px-3 py-2"
                >
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: group.color }}
                  />
                  <div className="text-left">
                    <div className="font-medium text-sm text-text-primary truncate max-w-[150px]">
                      {group.name}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {group.shots.filter(s => s.included).length} shots
                    </div>
                  </div>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleGroupVisibility(group.id);
                  }}
                  className="px-2 py-2 hover:bg-surface-light transition-colors border-l border-border"
                  title={visibleGroupIds.has(group.id) ? 'Hide group' : 'Show group'}
                >
                  {visibleGroupIds.has(group.id) ? (
                    <Eye size={16} className="text-text-secondary" />
                  ) : (
                    <EyeOff size={16} className="text-text-muted" />
                  )}
                </button>
              </div>
            ))}
          </div>

          {/* View Tabs */}
          <div className="flex gap-1">
            {VIEWS.map(view => {
              const Icon = view.icon;
              return (
                <button
                  key={view.id}
                  onClick={() => setActiveView(view.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
                    activeView === view.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-surface text-text-secondary hover:bg-surface-light'
                  }`}
                >
                  <Icon size={16} />
                  <span className="text-sm font-medium">{view.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sticky Metrics Bar - Reference Shot #1 */}
      {referenceShot && (
        <div className="bg-surface border-b border-border text-text-primary">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="flex items-center gap-6 overflow-x-auto text-xs">
              <div className="flex-shrink-0 font-semibold text-text-secondary">Shot #1:</div>
              <div className="flex gap-6">
                <div>
                  <span className="text-text-muted">Club Speed: </span>
                  <span className="font-mono text-text-primary">{referenceShot.metrics.clubSpeed?.toFixed(1)} mph</span>
                </div>
                <div>
                  <span className="text-text-muted">Ball Speed: </span>
                  <span className="font-mono text-text-primary">{referenceShot.metrics.ballSpeed?.toFixed(1)} mph</span>
                </div>
                <div>
                  <span className="text-text-muted">Smash Fac.: </span>
                  <span className="font-mono text-text-primary">{referenceShot.metrics.smashFactor?.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-text-muted">Launch Ang.: </span>
                  <span className="font-mono text-text-primary">{referenceShot.metrics.launchAngle?.toFixed(1)}°</span>
                </div>
                <div>
                  <span className="text-text-muted">Spin Rate: </span>
                  <span className="font-mono text-text-primary">{referenceShot.metrics.spinRate?.toFixed(0)} rpm</span>
                </div>
                <div>
                  <span className="text-text-muted">Carry: </span>
                  <span className="font-mono text-text-primary">{referenceShot.metrics.carry?.toFixed(1)} Yds</span>
                </div>
                <div>
                  <span className="text-text-muted">Total: </span>
                  <span className="font-mono text-text-primary">{referenceShot.metrics.total?.toFixed(1)} Yds</span>
                </div>
                <div>
                  <span className="text-text-muted">Land. Ang.: </span>
                  <span className="font-mono text-text-primary">{referenceShot.metrics.landingAngle?.toFixed(1)}°</span>
                </div>
                <div>
                  <span className="text-text-muted">Height: </span>
                  <span className="font-mono text-text-primary">{referenceShot.metrics.height?.toFixed(0)} Ft</span>
                </div>
                <div>
                  <span className="text-text-muted">Attack Ang.: </span>
                  <span className="font-mono text-text-primary">{referenceShot.metrics.attackAngle?.toFixed(1)}°</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* TrackMan-style Controls Bar */}
        <div className="mb-4 flex items-center justify-between bg-surface border border-border rounded-lg px-4 py-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-text-secondary">Distance Type:</span>
              <div className="flex gap-1 bg-background rounded-lg p-1">
                <button
                  onClick={() => setDistanceType('carry')}
                  className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    distanceType === 'carry'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  Carry
                </button>
                <button
                  onClick={() => setDistanceType('total')}
                  className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    distanceType === 'total'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-text-secondary hover:text-primary'
                  }`}
                >
                  Total
                </button>
              </div>
            </div>

            {/* Shot Selection Controls */}
            <div className="flex items-center gap-2 border-l border-border pl-4">
              <button
                onClick={() => setShowAverage(!showAverage)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-colors hover:bg-surface-light text-text-primary"
              >
                {showAverage ? <CheckSquare size={16} className="text-primary" /> : <Square size={16} />}
                <span>Show Average</span>
              </button>
              <span className="text-xs text-text-muted">
                {selectedShots.length} shot{selectedShots.length !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={handleSelectAll}
                className="px-2 py-1 text-xs font-medium rounded transition-colors hover:bg-surface-light text-text-secondary"
              >
                Select All
              </button>
              <button
                onClick={handleDeselectAll}
                className="px-2 py-1 text-xs font-medium rounded transition-colors hover:bg-surface-light text-text-secondary"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Charts Section - TrackMan Light Theme */}
        <div className="mb-6">
          {activeView === 'dispersion' && (
            <div>
              <ChartErrorBoundary chartName="Dispersion Chart">
                <div className="bg-surface border border-border rounded-lg p-6 mb-6 shadow-sm">
                  <h2 className="text-lg font-semibold text-text-primary mb-4">Shot Dispersion Pattern</h2>
                  <DispersionChart
                    shotGroups={visibleGroups}
                    distanceType={distanceType}
                    selectedShots={selectedShots}
                    showAverage={showAverage}
                  />
                </div>
              </ChartErrorBoundary>
              <ChartErrorBoundary chartName="Average Metrics">
                <AverageMetricsBar
                  shotGroups={visibleGroups}
                  highlightDistance={distanceType}
                  selectedShots={selectedShots}
                  showAverage={showAverage}
                />
              </ChartErrorBoundary>
            </div>
          )}

          {activeView === 'trajectory' && (
            <div>
              <ChartErrorBoundary chartName="Side View Trajectory">
                <div className="bg-surface border border-border rounded-lg p-6 mb-6 shadow-sm">
                  <h2 className="text-lg font-semibold text-text-primary mb-2">Ball Flight - Side View</h2>
                  <p className="text-sm text-text-secondary mb-4">Height vs Distance trajectory</p>
                  <TrajectoryChart
                    shotGroups={visibleGroups}
                    selectedShots={selectedShots}
                    showAverage={showAverage}
                    distanceType={distanceType}
                  />
                </div>
              </ChartErrorBoundary>
              <ChartErrorBoundary chartName="Average Metrics">
                <AverageMetricsBar
                  shotGroups={visibleGroups}
                  highlightDistance={distanceType}
                  selectedShots={selectedShots}
                  showAverage={showAverage}
                />
              </ChartErrorBoundary>
            </div>
          )}

          {activeView === 'golferpov' && (
            <div>
              <ChartErrorBoundary chartName="Golfer POV Trajectory">
                <div className="bg-surface border border-border rounded-lg p-6 mb-6 shadow-sm">
                  <h2 className="text-lg font-semibold text-text-primary mb-2">Ball Landing - Golfer POV (Tee Perspective)</h2>
                  <p className="text-sm text-text-secondary mb-4">Shows landing points with lateral dispersion (left/right) vs distance</p>
                  <GolferPOVTrajectory
                    shotGroups={visibleGroups}
                    selectedShots={selectedShots}
                    showAverage={showAverage}
                    distanceType={distanceType}
                  />
                </div>
              </ChartErrorBoundary>
              <ChartErrorBoundary chartName="Average Metrics">
                <AverageMetricsBar
                  shotGroups={visibleGroups}
                  highlightDistance={distanceType}
                  selectedShots={selectedShots}
                  showAverage={showAverage}
                />
              </ChartErrorBoundary>
            </div>
          )}

          {activeView === 'ai' && (
            <AIRecommendationsView
              shotGroups={visibleGroups}
              selectedShots={selectedShots}
              distanceType={distanceType}
            />
          )}

        </div>

        {/* Shot Group Tables */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-text-primary">Shot Data</h2>
          {sessionData.shotGroups.map(group => (
            <ShotGroupTable
              key={group.id}
              shotGroup={group}
              onToggleShot={handleToggleShot}
              onSelectShot={handleSelectShot}
              selectedShots={selectedShots}
              showAverage={showAverage}
            />
          ))}
        </div>
      </div>

      {/* Coaching Modal */}
      {showCoachingModal && (
        <CoachingModal
          shotGroups={sessionData.shotGroups}
          onClose={() => setShowCoachingModal(false)}
        />
      )}
    </div>
  );
}
